/**
 * Apply LG-2856 `XmlText` edits on the live v2 stream, and anchor a paragraph or caret with
 * `Y.RelativePosition` so an earlier insert does not move the target.
 *
 * A call is either all deletes or all inserts. Deletes run from the highest index downward inside
 * one transaction. A caret on a character uses assoc `0`. A caret at the end of finalized text
 * uses assoc `-1` at the insert gap, which resolves back to that gap.
 */

import * as Y from 'yjs'

import { ROOT_KEYS, editableCharIndex, editableInsertIndex, editableLength } from 'src/models/crdtV2'
import type { IndexedStream, XmlTextEdit } from 'src/models/crdtV2'
import { rangesFromStream } from 'src/models/crdtV2/incremental'

import { xmlTextToIndexedStream } from './yjsAdapter'

/** The live stream text, or `null` when the doc has no `XmlText` child yet. Does not insert one. */
const existingStreamText = (doc: Y.Doc): Y.XmlText | null => {
    const fragment = doc.get(ROOT_KEYS.text, Y.XmlFragment) as Y.XmlFragment
    const first = fragment.length > 0 ? fragment.get(0) : undefined

    return first instanceof Y.XmlText ? first : null
}

const isDelete = (edit: XmlTextEdit): edit is { index: number; delete: number } => 'delete' in edit

const isParagraphChangeAt = (stream: IndexedStream, index: number): boolean =>
    rangesFromStream(stream).some((range) => range.start === index)

const positionInStream = (doc: Y.Doc, anchor: Y.RelativePosition): { text: Y.XmlText; index: number } | null => {
    const text = existingStreamText(doc)
    if (text === null) {
        return null
    }

    const absolute = Y.createAbsolutePositionFromRelativePosition(anchor, doc)
    if (absolute === null || absolute.type !== text) {
        return null
    }

    return { text, index: absolute.index }
}

/** Transaction origin for a keystroke from the open editor. Remote flushes skip this origin. */
export const LOCAL_EDITOR_ORIGIN: unique symbol = Symbol('local-editor')

/**
 * Apply a homogeneous edit list to `text`.
 * Throws when the list mixes inserts and deletes, before any mutation.
 * `origin` is stamped on the `Y.Doc` transaction. Omitting it leaves the transaction untagged.
 */
export const applyXmlTextEdits = (text: Y.XmlText, edits: readonly XmlTextEdit[], origin?: unknown): void => {
    if (edits.length === 0) {
        return
    }

    const deletes = edits.filter(isDelete)
    if (deletes.length !== 0 && deletes.length !== edits.length) {
        throw new Error('applyXmlTextEdits expects either all inserts or all deletes')
    }

    const apply = () => {
        if (deletes.length > 0) {
            const ordered = [...deletes].sort((left, right) => right.index - left.index)
            for (const edit of ordered) {
                text.delete(edit.index, edit.delete)
            }

            return
        }

        for (const edit of edits) {
            if (!isDelete(edit)) {
                text.insert(edit.index, edit.insert)
            }
        }
    }

    const doc = text.doc
    if (doc === null) {
        apply()

        return
    }

    if (origin === undefined) {
        doc.transact(apply)

        return
    }

    doc.transact(apply, origin)
}

/** Relative position of the paragraph's `paragraph_change` marker, or `null` when it is absent. */
export const createParagraphAnchor = (doc: Y.Doc, paragraphId: string): Y.RelativePosition | null => {
    const text = existingStreamText(doc)
    if (text === null) {
        return null
    }

    const range = rangesFromStream(xmlTextToIndexedStream(text)).find((item) => item.id === paragraphId)
    if (range === undefined) {
        return null
    }

    return Y.createRelativePositionFromTypeIndex(text, range.start)
}

/**
 * Absolute index of the anchored `paragraph_change`, or `null` when the position is missing
 * or no longer points at that kind of embed.
 */
export const resolveParagraphAnchor = (doc: Y.Doc, anchor: Y.RelativePosition): number | null => {
    const position = positionInStream(doc, anchor)
    if (position === null) {
        return null
    }

    const stream = xmlTextToIndexedStream(position.text)

    return isParagraphChangeAt(stream, position.index) ? position.index : null
}

/**
 * Relative position of an editable caret. `editableOffset === editableLength` is the gap after
 * the last finalized character.
 */
export const createCaretAnchor = (doc: Y.Doc, paragraphId: string, editableOffset: number): Y.RelativePosition | null => {
    const text = existingStreamText(doc)
    if (text === null) {
        return null
    }

    const stream = xmlTextToIndexedStream(text)
    const length = editableLength(stream, paragraphId)
    if (length === null || !Number.isFinite(editableOffset) || editableOffset < 0 || editableOffset > length) {
        return null
    }

    const atEnd = editableOffset === length
    const index = atEnd ? editableInsertIndex(stream, paragraphId, editableOffset) : editableCharIndex(stream, paragraphId, editableOffset)
    if (index === null) {
        return null
    }

    return Y.createRelativePositionFromTypeIndex(text, index, atEnd ? -1 : 0)
}

/** Absolute `XmlText` index of a caret anchor, or `null` when it does not resolve in this doc. */
export const resolveCaretAnchor = (doc: Y.Doc, anchor: Y.RelativePosition): number | null => positionInStream(doc, anchor)?.index ?? null
